
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AiSignal, BotPerformance, SmartMoneyTrader } from '../types/ai';
import { AiService } from '../lib/ai'; // Fallback interface
import { AiEngine } from '../lib/ai/engine'; // Advanced engine
import { TradingAgentSettings, useTradingAgent } from '../hooks/use-trading-agent';
import { useNotifications } from '../context/NotificationContext';
import { useWallet } from './WalletProvider';

interface AiContextType {
    signals: AiSignal[];
    smartMoney: SmartMoneyTrader[];
    performance: BotPerformance | null;
    isBotActive: boolean;
    isLoading: boolean;
    agentSettings: TradingAgentSettings;
    updateAgentSettings: (settings: TradingAgentSettings) => void;
    toggleBot: () => void;
    refreshData: () => Promise<void>;
}

const AiContext = createContext<AiContextType | undefined>(undefined);

export const AiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [signals, setSignals] = useState<AiSignal[]>([]);
    const [smartMoney, setSmartMoney] = useState<SmartMoneyTrader[]>([]);
    const [performance, setPerformance] = useState<BotPerformance | null>(null);
    const [isBotActive, setIsBotActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Agent Logic
    const { settings: agentSettings, saveSettings: updateAgentSettings } = useTradingAgent();
    const { addNotification } = useNotifications();
    const { balance } = useWallet();
    const prevSignalsRef = useRef<AiSignal[]>([]);

    const refreshData = async () => {
        setIsLoading(true);
        try {
            // Fetch comprehensive data from the Engine
            const [newSignals, perf, traders] = await Promise.all([
                AiEngine.scanForSignals(),
                AiEngine.getBotPerformance(),
                AiEngine.getSmartMoneyTraders()
            ]);
            
            setSignals(newSignals);
            setSmartMoney(traders);
            
            if (!performance) { // Only set initial performance
                setPerformance(perf);
            }
        } catch (e) {
            console.error("AI Provider Error:", e);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleBot = useCallback(() => setIsBotActive(prev => !prev), []);

    // Initial load
    useEffect(() => {
        refreshData();
    }, []);

    // Effect for auto-trading simulation
    useEffect(() => {
        if (isBotActive && signals.length > 0 && performance) {
            const newSignals = signals.filter(s => !prevSignalsRef.current.find(ps => ps.id === s.id));
            
            if (newSignals.length > 0) {
                console.log(`[Auto-Trader] Detected ${newSignals.length} new signals. Bot is active.`);
                
                newSignals.forEach(signal => {
                    // Check drawdown/profit target against native balance
                    const drawdownLimit = balance * (agentSettings.dailyDrawdown / 100);
                    const profitLimit = balance * (agentSettings.profitTarget / 100);

                    if (performance.dailyPnl < -drawdownLimit) {
                        console.log(`[Auto-Trader] Daily drawdown limit of ${agentSettings.dailyDrawdown}% reached. Pausing trading.`);
                        addNotification({ type: 'warning', title: 'Auto-Trader Paused', message: 'Daily drawdown limit reached. Trading is paused for today.' });
                        toggleBot();
                        return;
                    }
                    if (performance.dailyPnl > profitLimit && profitLimit > 0) {
                        console.log(`[Auto-Trader] Daily profit target of ${agentSettings.profitTarget}% reached. Pausing trading.`);
                        addNotification({ type: 'success', title: 'Auto-Trader Paused', message: 'Daily profit target reached. Trading is paused for today.' });
                        toggleBot();
                        return;
                    }

                    let confidenceThreshold = 75; // Default for Aggressive
                    if (agentSettings.riskLevel === 'Conservative') confidenceThreshold = 90;
                    if (agentSettings.riskLevel === 'Balanced') confidenceThreshold = 80;

                    if (signal.confidence >= confidenceThreshold) {
                        // Simulate trade PNL: 5-20% gain/loss on the trade size
                        const tradePnl = (Math.random() > 0.5 ? 1 : -1) * agentSettings.tradeSize * (Math.random() * 0.15 + 0.05);
                        
                        setPerformance(prev => prev ? ({
                            ...prev,
                            dailyPnl: prev.dailyPnl + tradePnl,
                            totalProfit: prev.totalProfit + tradePnl,
                            winRate: tradePnl > 0 ? Math.min(100, prev.winRate + 0.5) : Math.max(0, prev.winRate - 0.5),
                        }) : null);

                        addNotification({
                            type: 'success',
                            title: `Auto-Trader Executed ${signal.type}`,
                            message: `Executed ${signal.type} on ${signal.token.symbol} for ${agentSettings.tradeSize} SOL. Confidence: ${signal.confidence}%. Est. PnL: ${tradePnl.toFixed(2)} SOL.`,
                        });
                    } else {
                         console.log(`[Auto-Trader] SKIPPING trade for ${signal.token.symbol}. Confidence ${signal.confidence}% below threshold ${confidenceThreshold}% for ${agentSettings.riskLevel} profile.`);
                    }
                });
            }
        }
        prevSignalsRef.current = signals;
    }, [signals, isBotActive, agentSettings, addNotification, balance, performance, toggleBot]);

    return React.createElement(AiContext.Provider, {
        value: {
            signals,
            smartMoney,
            performance,
            isBotActive,
            isLoading,
            agentSettings,
            updateAgentSettings,
            toggleBot,
            refreshData
        }
    }, children);
};

export const useAiContext = () => {
    const context = useContext(AiContext);
    if (!context) throw new Error("useAiContext must be used within AiProvider");
    return context;
};
